import { calendarData } from "./calendarData";

// Simulated API service functions with delay to mimic network requests
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

export const calendarService = {
  // Get all workspaces and meeting rooms
  getWorkspaces: async (type = "all") => {
    await delay(400);

    let spaces = [];

    if (type === "workspace") {
      spaces = calendarData.workspaces;
    } else if (type === "meeting_room") {
      spaces = calendarData.meetingRooms;
    } else {
      spaces = [...calendarData.workspaces, ...calendarData.meetingRooms];
    }

    return {
      data: spaces,
      status: 200,
    };
  },

  // Get workspace/meeting room by ID
  getWorkspaceById: async id => {
    await delay(300);

    const allSpaces = [...calendarData.workspaces, ...calendarData.meetingRooms];
    const workspace = allSpaces.find(space => space.id === parseInt(id));

    if (!workspace) {
      return {
        data: null,
        status: 404,
        message: "Workspace not found",
      };
    }

    return {
      data: workspace,
      status: 200,
    };
  },

  // Get bookings with filtering options
  getBookings: async (filters = {}) => {
    await delay(500);

    let filteredBookings = [...calendarData.bookings];

    // Filter by date
    if (filters.date) {
      const filterDate = new Date(filters.date);
      filteredBookings = filteredBookings.filter(booking => {
        const bookingDate = new Date(booking.date);
        return bookingDate.toDateString() === filterDate.toDateString();
      });
    }

    // Filter by workspace
    if (filters.workspaceId) {
      filteredBookings = filteredBookings.filter(booking => booking.workspaceId === parseInt(filters.workspaceId));
    }

    // Filter by status
    if (filters.status) {
      filteredBookings = filteredBookings.filter(booking => booking.status === filters.status);
    }

    // Filter by date range
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      filteredBookings = filteredBookings.filter(booking => {
        const bookingDate = new Date(booking.date);
        return bookingDate >= startDate && bookingDate <= endDate;
      });
    }

    // Search by name or email
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredBookings = filteredBookings.filter(
        booking =>
          booking.name.toLowerCase().includes(searchTerm) ||
          booking.email.toLowerCase().includes(searchTerm) ||
          booking.company.toLowerCase().includes(searchTerm)
      );
    }

    return {
      data: filteredBookings,
      total: filteredBookings.length,
      status: 200,
    };
  },

  // Get booking by ID
  getBookingById: async id => {
    await delay(300);

    const booking = calendarData.bookings.find(b => b.id === parseInt(id));

    if (!booking) {
      return {
        data: null,
        status: 404,
        message: "Booking not found",
      };
    }

    return {
      data: booking,
      status: 200,
    };
  },

  // Create new booking
  createBooking: async bookingData => {
    await delay(800);

    // Check for conflicts
    const conflicts = calendarData.bookings.filter(booking => {
      const isSameWorkspace = booking.workspaceId === bookingData.workspaceId;
      const isSameDate = new Date(booking.date).toDateString() === new Date(bookingData.date).toDateString();

      if (!isSameWorkspace || !isSameDate) return false;

      const existingStart = new Date(booking.startTime);
      const existingEnd = new Date(booking.endTime);
      const newStart = new Date(bookingData.startTime);
      const newEnd = new Date(bookingData.endTime);

      return newStart < existingEnd && newEnd > existingStart;
    });

    if (conflicts.length > 0) {
      return {
        data: null,
        status: 409,
        message: "Time slot conflicts with existing booking",
        conflicts: conflicts,
      };
    }

    const newBooking = {
      id: Math.max(...calendarData.bookings.map(b => b.id)) + 1,
      status: "pending",
      paymentStatus: "pending",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face",
      ...bookingData,
      startTime: new Date(bookingData.startTime),
      endTime: new Date(bookingData.endTime),
      date: new Date(bookingData.date),
    };

    // Calculate total amount
    const workspace = [...calendarData.workspaces, ...calendarData.meetingRooms].find(
      w => w.id === bookingData.workspaceId
    );

    if (workspace) {
      const duration = (newBooking.endTime - newBooking.startTime) / (1000 * 60 * 60); // hours
      newBooking.totalAmount = duration * workspace.hourlyRate;
    }

    calendarData.bookings.push(newBooking);

    return {
      data: newBooking,
      status: 201,
      message: "Booking created successfully",
    };
  },

  // Update booking
  updateBooking: async (id, updates) => {
    await delay(600);

    const bookingIndex = calendarData.bookings.findIndex(b => b.id === parseInt(id));

    if (bookingIndex === -1) {
      return {
        data: null,
        status: 404,
        message: "Booking not found",
      };
    }

    // Check for conflicts if time or workspace is being updated
    if (updates.startTime || updates.endTime || updates.workspaceId || updates.date) {
      const updatedBooking = {
        ...calendarData.bookings[bookingIndex],
        ...updates,
      };

      const conflicts = calendarData.bookings.filter((booking, index) => {
        if (index === bookingIndex) return false; // Skip the booking being updated

        const isSameWorkspace = booking.workspaceId === updatedBooking.workspaceId;
        const isSameDate = new Date(booking.date).toDateString() === new Date(updatedBooking.date).toDateString();

        if (!isSameWorkspace || !isSameDate) return false;

        const existingStart = new Date(booking.startTime);
        const existingEnd = new Date(booking.endTime);
        const newStart = new Date(updatedBooking.startTime);
        const newEnd = new Date(updatedBooking.endTime);

        return newStart < existingEnd && newEnd > existingStart;
      });

      if (conflicts.length > 0) {
        return {
          data: null,
          status: 409,
          message: "Time slot conflicts with existing booking",
          conflicts: conflicts,
        };
      }
    }

    // Update the booking
    calendarData.bookings[bookingIndex] = {
      ...calendarData.bookings[bookingIndex],
      ...updates,
    };

    // Recalculate amount if time or workspace changed
    if (updates.startTime || updates.endTime || updates.workspaceId) {
      const booking = calendarData.bookings[bookingIndex];
      const workspace = [...calendarData.workspaces, ...calendarData.meetingRooms].find(
        w => w.id === booking.workspaceId
      );

      if (workspace) {
        const duration = (new Date(booking.endTime) - new Date(booking.startTime)) / (1000 * 60 * 60);
        calendarData.bookings[bookingIndex].totalAmount = duration * workspace.hourlyRate;
      }
    }

    return {
      data: calendarData.bookings[bookingIndex],
      status: 200,
      message: "Booking updated successfully",
    };
  },

  // Cancel booking
  cancelBooking: async (id, reason = "") => {
    await delay(500);

    const bookingIndex = calendarData.bookings.findIndex(b => b.id === parseInt(id));

    if (bookingIndex === -1) {
      return {
        data: null,
        status: 404,
        message: "Booking not found",
      };
    }

    calendarData.bookings[bookingIndex].status = "cancelled";
    calendarData.bookings[bookingIndex].cancellationReason = reason;
    calendarData.bookings[bookingIndex].cancelledAt = new Date().toISOString();

    return {
      data: calendarData.bookings[bookingIndex],
      status: 200,
      message: "Booking cancelled successfully",
    };
  },

  // Delete booking
  deleteBooking: async id => {
    await delay(400);

    const bookingIndex = calendarData.bookings.findIndex(b => b.id === parseInt(id));

    if (bookingIndex === -1) {
      return {
        data: null,
        status: 404,
        message: "Booking not found",
      };
    }

    const deletedBooking = calendarData.bookings.splice(bookingIndex, 1)[0];

    return {
      data: deletedBooking,
      status: 200,
      message: "Booking deleted successfully",
    };
  },

  // Get available time slots for a workspace on a specific date
  getAvailableTimeSlots: async (workspaceId, date) => {
    await delay(400);

    const bookingsForDate = calendarData.bookings.filter(booking => {
      const isSameWorkspace = booking.workspaceId === parseInt(workspaceId);
      const isSameDate = new Date(booking.date).toDateString() === new Date(date).toDateString();
      return isSameWorkspace && isSameDate && booking.status !== "cancelled";
    });

    const availableSlots = calendarData.timeSlots.map(slot => {
      const isBooked = bookingsForDate.some(booking => {
        const bookingStart = new Date(booking.startTime).getHours();
        const bookingEnd = new Date(booking.endTime).getHours();
        return slot.hour >= bookingStart && slot.hour < bookingEnd;
      });

      return {
        ...slot,
        available: !isBooked,
      };
    });

    return {
      data: availableSlots,
      status: 200,
    };
  },

  // Get calendar statistics
  getCalendarStatistics: async (dateRange = {}) => {
    await delay(400);

    let filteredBookings = calendarData.bookings;

    // Filter by date range if provided
    if (dateRange.startDate && dateRange.endDate) {
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      filteredBookings = filteredBookings.filter(booking => {
        const bookingDate = new Date(booking.date);
        return bookingDate >= startDate && bookingDate <= endDate;
      });
    }

    const stats = {
      totalBookings: filteredBookings.length,
      confirmedBookings: filteredBookings.filter(b => b.status === "confirmed").length,
      pendingBookings: filteredBookings.filter(b => b.status === "pending").length,
      cancelledBookings: filteredBookings.filter(b => b.status === "cancelled").length,
      completedBookings: filteredBookings.filter(b => b.status === "completed").length,
      totalRevenue: filteredBookings.filter(b => b.paymentStatus === "paid").reduce((sum, b) => sum + b.totalAmount, 0),
      pendingRevenue: filteredBookings
        .filter(b => b.paymentStatus === "pending")
        .reduce((sum, b) => sum + b.totalAmount, 0),
      averageBookingDuration:
        filteredBookings.length > 0
          ? filteredBookings.reduce((sum, b) => {
              const duration = (new Date(b.endTime) - new Date(b.startTime)) / (1000 * 60 * 60);
              return sum + duration;
            }, 0) / filteredBookings.length
          : 0,
      occupancyRate: Math.round(
        (filteredBookings.length / (calendarData.workspaces.length + calendarData.meetingRooms.length)) * 100
      ),
    };

    return {
      data: stats,
      status: 200,
    };
  },

  // Get bookings for a specific month
  getMonthlyBookings: async (year, month) => {
    await delay(500);

    const monthlyBookings = calendarData.bookings.filter(booking => {
      const bookingDate = new Date(booking.date);
      return bookingDate.getFullYear() === year && bookingDate.getMonth() === month;
    });

    return {
      data: monthlyBookings,
      status: 200,
    };
  },

  // Search workspaces
  searchWorkspaces: async (query, type = "all") => {
    await delay(300);

    let spaces = [];

    if (type === "workspace") {
      spaces = calendarData.workspaces;
    } else if (type === "meeting_room") {
      spaces = calendarData.meetingRooms;
    } else {
      spaces = [...calendarData.workspaces, ...calendarData.meetingRooms];
    }

    if (query) {
      const searchTerm = query.toLowerCase();
      spaces = spaces.filter(
        space =>
          space.name.toLowerCase().includes(searchTerm) ||
          space.location.toLowerCase().includes(searchTerm) ||
          space.capacity.toLowerCase().includes(searchTerm)
      );
    }

    return {
      data: spaces,
      status: 200,
    };
  },

  // Bulk operations
  bulkUpdateBookings: async (bookingIds, updates) => {
    await delay(800);

    const updatedBookings = [];

    bookingIds.forEach(id => {
      const bookingIndex = calendarData.bookings.findIndex(b => b.id === parseInt(id));
      if (bookingIndex !== -1) {
        calendarData.bookings[bookingIndex] = {
          ...calendarData.bookings[bookingIndex],
          ...updates,
        };
        updatedBookings.push(calendarData.bookings[bookingIndex]);
      }
    });

    return {
      data: updatedBookings,
      status: 200,
      message: `${updatedBookings.length} bookings updated successfully`,
    };
  },

  // Get time slots
  getTimeSlots: async () => {
    await delay(200);

    return {
      data: calendarData.timeSlots,
      status: 200,
    };
  },

  // Get status options
  getStatusOptions: async () => {
    await delay(200);

    return {
      data: calendarData.statusOptions,
      status: 200,
    };
  },
};
